import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service';
import { AuthManager } from 'src/app/shared/auth/AuthManager';

@Component({
   selector: 'vc-assgin-new-goal-page',
   templateUrl: './assgin-new-goal-page.component.html',
   styleUrls: ['./assgin-new-goal-page.component.scss'],
   standalone: false,
})
export class AssginNewGoalPageComponent implements OnInit {

   // isLoading is for the full screen cat spinner (initial data fetch)
   isLoading = false;

   // loading is for the Save button spinner
   loading = false;

   goalDictionaryData: any;
   filteredGoals: any = [];
   searchQuery: string = '';
   userRecommendedCheckBox: boolean = false;
   goalData: any;
   goalDataValue: any[] = [];
   userRecommended: any;
   isAdmin = false;
   expandedGoalId: any = null;


   constructor(@Inject(MAT_DIALOG_DATA) private data: any,
      private dialogRef: MatDialogRef<AssginNewGoalPageComponent>,
      public leaderService: LeaderService,
      private authManager: AuthManager) { }

   ngOnInit(): void {
      this.authManager.getCurrentUser().subscribe((user: any) => {
         this.isAdmin = !!user?.roles?.some((role: any) => role?.roleName === 'Admin');
      });
      this.getGoalDictionary();
   }

   onClose() {
      this.dialogRef.close();
   }

   getGoalDictionary() {
      this.isLoading = true;
      this.leaderService.getGoalsForHospitalTarget(this.data.hospital.practiceSKey, this.data.quarter.quarterId).subscribe({
         next: (res: any) => {
            if (!res || !res.data) {
               this.isLoading = false;
               return;
            }
            this.goalDictionaryData = (res.data || []).map((goal: any) => ({
               ...goal,
               isRecommended: !!(goal?.isRecommended ?? goal?.recommendedFlag),
               isHM: false,
               recommendationReason: goal?.recommendationReason ?? goal?.recommendationReason ?? null,
               recommendedTarget: goal?.recommendedTarget != null
                  ? parseFloat(Number(goal.recommendedTarget).toFixed(2))
                  : goal?.recommendedTarget
            }));
            this.filteredGoals = [...this.goalDictionaryData];
            this.isLoading = false;
         },
         error: () => {
            this.isLoading = false;
         }
      });
   }

   onSearchChange(searchValue: string) {
      const normalizedSearch = (searchValue || '').toLowerCase();
      this.searchQuery = normalizedSearch;

      if (!normalizedSearch.trim()) {
         this.filteredGoals = this.goalDictionaryData || [];
         return;
      }

      const sourceGoals = this.goalDictionaryData || [];
      this.filteredGoals = sourceGoals.filter((goal: any) =>
         (goal?.goalName || '').toLowerCase().includes(normalizedSearch)
      );
   }


   onGoalTargetChange(event: any, id: any) {
      const isChecked = event.target.checked;
      const goal = this.filteredGoals.find((g: any) => g.goalId === id);
      if (!goal) {
         return;
      }

      goal.targetExceeded = false;
      const recommendedTargetForSave = (goal?.recommendedTarget);

      if (isChecked) {
         let goalObj = {
            "GoalId": id,
            "QuarterEligible": this.data.quarter.quarterName,
            "HospitalPracticeSKey": this.data.hospital.practiceSKey,
            "SubmittedTarget": recommendedTargetForSave,
            "RecommendedFlag": !!goal?.isRecommended,
            "IsHM": !!goal?.isHM,
            "QuarterId": this.data.quarter.quarterId,
            "recommendationReason": null
         };
         this.goalDataValue.push(goalObj);
      } else {
         // Remove from array when unchecked
         this.goalDataValue = this.goalDataValue.filter((g: any) => g.GoalId !== id);
      }

   }

   onRecommendedChange(goalId: any, isRecommended: boolean) {
      const goalInArray = this.goalDataValue.find((g: any) => g.GoalId === goalId);
      if (goalInArray) {
         goalInArray.RecommendedFlag = isRecommended;
      }
   }

   onHmToggleChange(goalId: any, isHM: boolean) {
      const goalInArray = this.goalDataValue.find((g: any) => g.GoalId === goalId);
      if (goalInArray) {
         goalInArray.IsHM = isHM;
      }
   }

   canEditRecommended(goal: any): boolean {

      return this.isAdmin || !goal?.isGoalTarget;
   }

   private sanitizeRecommendedTarget(value: any): number | null {
      if (value === null || value === undefined || value === '') {
         return null;
      }

      const parsedValue = Number(value);
      if (Number.isNaN(parsedValue)) {
         return null;
      }

      if (parsedValue < 0) {
         return null;
      }

      if (parsedValue > 100) {
         return null;
      }

      return parsedValue;
   }

   onRecommendedTargetChange(goal: any) {
      if (!goal) {
         return;
      }
      if (goal.recommendedType == '%') {
         const parsedValue = Number(goal.recommendedTarget);
         goal.targetExceeded = !Number.isNaN(parsedValue) && parsedValue > 100;
         goal.recommendedTarget = this.sanitizeRecommendedTarget(goal.recommendedTarget);

         const selectedGoal = this.goalDataValue.find((g: any) => g.GoalId === goal.goalId);
         if (selectedGoal) {
            selectedGoal.SubmittedTarget = goal.recommendedTarget;
         }
      }
      else {
         goal.recommendedTarget = goal.recommendedTarget;
         const selectedGoal = this.goalDataValue.find((g: any) => g.GoalId === goal.goalId);
         if (selectedGoal) {
            selectedGoal.SubmittedTarget = goal.recommendedTarget;
         }
      }
   }

   formatBaselineAsPercent(baseline: any): string {
      if (baseline === null || baseline === undefined || baseline === '') {
         return '-';
      }

      const baselineText = String(baseline).trim();
      if (!baselineText) {
         return '-';
      }

      if (baselineText.includes('%')) {
         return baselineText.replace(/%/g, '').trim();
      }

      const numericBaseline = Number(baselineText);
      if (Number.isNaN(numericBaseline)) {
         return baselineText;
      }

      return `${numericBaseline.toFixed(2)}`;
   }

   isTargetDifferent(goal: any): boolean {
      const recommended = goal?.recommended;
      const target = goal?.recommendedTarget;
      if (recommended === null || recommended === undefined || target === null || target === undefined || target === '') {
         return false;
      }
      return Number(recommended) !== Number(target);
   }

   getRecommendationReasonDisplay(goal: any): string {
      const rawReason = goal?.recommendationReason ?? goal?.recommendationReason;
      const reason = typeof rawReason === 'string' ? rawReason.trim() : '';

      return reason ? reason : '-';
   }

   toggleGoalDetails(goalId: any, event?: Event): void {
      if (event) {
         event.stopPropagation();
      }
      this.expandedGoalId = this.expandedGoalId === goalId ? null : goalId;
   }

   hasGoalDetailContent(goal: any): boolean {
      return !!(goal?.description || goal?.explanation || goal?.calcDisplay);
   }

   preventNegativeKey(event: KeyboardEvent): void {
      if (event.key === '-' || event.code === 'Minus' || event.code === 'NumpadSubtract') {
         event.preventDefault();
      }
   }

   onSave() {
      if (this.goalDataValue.length === 0) {
         this.leaderService.showNotification('Please select at least one goal to assign targets', true);
         return;
      }

      // Validate reasoning for goals where target differs from recommended
      const goalsNeedingReasoning = this.goalDataValue.filter((goalObj: any) => {
         const goal = this.filteredGoals.find((g: any) => g.goalId === goalObj.GoalId);
         return goal && this.isTargetDifferent(goal);
      });

      for (const goalObj of goalsNeedingReasoning) {
         const goal = this.filteredGoals.find((g: any) => g.goalId === goalObj.GoalId);
         if (goal && (!goal.reasoning || !goal.reasoning.trim())) {
            goal.reasoningRequired = true;
            this.leaderService.showNotification('Please provide reasoning for all goals where target differs from recommended.', true);
            return;
         }
      }

      // Include reasoning as Comments in the payload — backend handles posting them after creation
      for (const goalObj of this.goalDataValue) {
         const goal = this.filteredGoals.find((g: any) => g.goalId === goalObj.GoalId);
         if (goal && this.isTargetDifferent(goal) && goal.reasoning?.trim()) {
            goalObj.Comments = `Target Reasoning: ${goal.reasoning.trim()}`;

            goalObj.recommendationReason = goal.reasoning.trim();
         }
      }

      this.loading = true;
      this.leaderService.goalTargetAssignment(this.goalDataValue).subscribe({
         next: (res: any) => {
            if (res.resultDetails[0].status !== 'FAILURE') {
               this.loading = false;
               this.dialogRef.close('success');
               this.leaderService.showNotification('Goal targets assigned successfully', false);
            }
            else {
               this.loading = false;
               this.leaderService.showNotification('Failed to assign goal targets', true);
            }
         },
         error: () => {
            this.loading = false;
         }
      });
   }
}
